/* ============================================================
   ONEBAR TRADING — FX LAYER
   ------------------------------------------------------------
   ONE effect is left in this file: the optional 3D sculpt.

   THREE THINGS WERE DELETED ON 2026-08-13, AND THE REASONS MATTER
   MORE THAN THE CODE THAT WENT.

   1. MAGNET — every .obt-btn on the page drifted toward the pointer.
      It was written as "one rAF for the whole page, zero re-render
      cost", and the rAF was not the cost. Inside it the handler
      called getBoundingClientRect() on all 29 buttons of the
      homepage, on every pointer move: 29 forced synchronous layouts
      per frame for the entire time the mouse was in motion. That is
      the single biggest reason the site felt heavy under the cursor.
      It was also a correctness bug. PULL 0.24 with RANGE 70 lets a
      242px-wide button travel 46px sideways while the two hero CTAs
      sit 16px apart, so they physically overlapped — the screenshot
      Mr. Markov sent. A control that moves away from the cursor is
      also a control that is harder to click, and it cannot be given
      to a keyboard or a touch user at all.

   2. FIELD — a full-screen WebGL fragment shader (two orbiting light
      lobes over 4-octave value noise) painting at 30fps behind the
      whole site for as long as the tab was open. Half resolution and
      capability-gated, and still a permanent GPU job under every
      scroll. obt-ui.css already carried three static radial washes
      as its fallback and the old comment in this file admitted they
      were "deliberately good enough to ship on its own". They are.

   3. SPOT — a radial highlight on card borders, positioned from an
      unthrottled document-level pointermove that read a bounding
      rect per event. Replaced by a static lit edge in obt-ui.css.

   Do not reintroduce any of the three. Pointer-tracked decoration is
   the category of effect this brand refuses: it is motion that exists
   because it is possible, it costs layout on the main thread, and it
   is invisible to half the people using the site.
   ============================================================ */
(function () {
  'use strict';

  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || {};
  var lowPower =
    conn.saveData === true ||
    /2g/.test(conn.effectiveType || '') ||
    (navigator.deviceMemory && navigator.deviceMemory < 4) ||
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4);

  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };

  /* ----------------------------------------------------------
     THREE — a real 3D object where a section asks for it.

     Markup contract:
       <div class="obt-3d" data-obt-3d="bull"
            data-src="{{ 'obt-bull.glb' | asset_url }}"
            data-lib="{{ 'obt-three.js' | asset_url }}"></div>

     The section MUST still render its flat image inside that div.
     The image is the LCP element and stays until the model is ready,
     which is why loading three.js can never regress LCP. Both data
     attributes are emitted only when the merchant has actually named
     a model in the section settings, so on a store that has not, this
     whole branch returns on its first line and nothing downloads.
     ---------------------------------------------------------- */
  var LIB_FALLBACK = null;

  /* Injects the bundle once and resolves with the global it defines.
     Repeated calls share one promise, so a second 3D host on the page
     cannot trigger a second 569KB download. */
  var libPromise = null;
  function loadLib(url) {
    if (libPromise) return libPromise;
    libPromise = new Promise(function (resolve, reject) {
      if (window.OBT_THREE) return resolve(window.OBT_THREE);
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = function () {
        window.OBT_THREE ? resolve(window.OBT_THREE) : reject(new Error('obt-three: global missing'));
      };
      s.onerror = function () { reject(new Error('obt-three: load failed')); };
      document.head.appendChild(s);
    });
    return libPromise;
  }

  function mountThree() {
    if (reduce || lowPower) return;
    if (innerWidth < 1024) return;                        // desktop only
    var host = document.querySelector('[data-obt-3d]');
    if (!host || !host.dataset.src) return;
    if (!host.dataset.lib && !LIB_FALLBACK) return;   // no library, no 3D

    var seen = false;
    var io = new IntersectionObserver(function (entries) {
      if (seen || !entries[0].isIntersecting) return;
      seen = true; io.disconnect();
      boot(host);
    }, { rootMargin: '200px' });
    io.observe(host);
  }

  function boot(host) {
    /* A classic <script> tag, deliberately NOT a dynamic import().
       Shopify's asset pipeline transpiles theme JS and downlevels
       `import(x)` into `Promise.resolve().then(() => __toESM(require(x)))`.
       `require` does not exist in a browser, so the promise rejected on
       every page load and the catch below swallowed it in silence — the
       3D layer looked wired and had never once run. Injecting a plain
       script and reading a global sidesteps module semantics entirely,
       so nothing downstream can rewrite it. */
    loadLib(host.dataset.lib || LIB_FALLBACK).then(function (LIB) {
      var THREE = LIB, GLTFLoader = LIB.GLTFLoader, MeshoptDecoder = LIB.MeshoptDecoder;

      var w = host.clientWidth, h = host.clientHeight;
      var renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
      renderer.setSize(w, h);
      renderer.domElement.setAttribute('aria-hidden', 'true');
      renderer.domElement.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;opacity:0;transition:opacity 700ms cubic-bezier(.22,1,.36,1)';

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(34, w / h, 0.1, 100);
      camera.position.set(0, 0.1, 4.2);

      scene.add(new THREE.HemisphereLight(0xffffff, 0xdfe6da, 1.15));
      var key = new THREE.DirectionalLight(0xffffff, 1.5); key.position.set(2.5, 3, 3); scene.add(key);
      var rim = new THREE.DirectionalLight(0xc8102e, 1.1); rim.position.set(-3, 1, -2); scene.add(rim);

      new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).load(host.dataset.src, function (gltf) {
        var obj = gltf.scene;
        var box = new THREE.Box3().setFromObject(obj);
        var size = box.getSize(new THREE.Vector3());
        var s = 2.2 / Math.max(size.x, size.y, size.z);
        obj.scale.setScalar(s);
        box.setFromObject(obj);
        var c = box.getCenter(new THREE.Vector3());
        obj.position.sub(c);
        scene.add(obj);

        host.style.position = 'relative';
        host.appendChild(renderer.domElement);
        requestAnimationFrame(function () { renderer.domElement.style.opacity = '1'; });
        var flat = host.querySelector('img');
        if (flat) { flat.style.transition = 'opacity 700ms cubic-bezier(.22,1,.36,1)'; flat.style.opacity = '0'; }

        var tx = 0, ty = 0, cx = 0, cy = 0, alive = true;
        addEventListener('pointermove', function (e) {
          tx = (e.clientX / innerWidth - 0.5) * 0.5;
          ty = (e.clientY / innerHeight - 0.5) * 0.3;
        }, { passive: true });

        var vis = new IntersectionObserver(function (en) { alive = en[0].isIntersecting; if (alive) tick(); }, { rootMargin: '120px' });
        vis.observe(host);
        document.addEventListener('visibilitychange', function () { if (!document.hidden && alive) tick(); });

        var t0 = performance.now(), queued = false;
        function tick() {
          if (queued) return;
          queued = true;
          requestAnimationFrame(function (now) {
            queued = false;
            if (!alive || document.hidden) return;
            cx += (tx - cx) * 0.06; cy += (ty - cy) * 0.06;
            obj.rotation.y = cx + Math.sin((now - t0) / 4200) * 0.10;
            obj.rotation.x = cy * 0.6 + Math.sin((now - t0) / 5600) * 0.04;
            obj.position.y = -c.y * 0 + Math.sin((now - t0) / 3400) * 0.045;
            renderer.render(scene, camera);
            tick();
          });
        }
        tick();

        addEventListener('resize', function () {
          var nw = host.clientWidth, nh = host.clientHeight;
          camera.aspect = nw / nh; camera.updateProjectionMatrix(); renderer.setSize(nw, nh);
        }, { passive: true });
      }, null, function () { /* model missing: the flat image simply stays */ });
    }).catch(function () { /* CDN blocked: the flat image simply stays */ });
  }

  /* ---------------------------------------------------------- */
  function start() { idle(mountThree); }
  if (document.readyState === 'complete') start();
  else addEventListener('load', start, { once: true });
})();
